import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  PlayCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { leadService } from '../../services/leadService';
import { campaignService } from '../../services/campaignService';
import { agentService } from '../../services/agentService';
import { Lead, LeadStatus, LeadSubStage, CallAttemptStats } from '../../types/lead';
import { Campaign, CampaignStats, CampaignResultFilters } from '../../types/campaign';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Breadcrumb from '../../components/UI/Breadcrumb';
import Button from '../../components/UI/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import LeadDetailSidebar from '../../components/Leads/LeadDetailSidebar';
import LeadResultCard from '../../components/Leads/LeadResultCard';
import SearchableSelect from '../../components/UI/SearchableSelect';
import SmsCampaignResultsView from './SmsCampaignResultsView';

type PipelineTab = 'all' | 'interested' | 'follow_up' | 'app_out' | 'app_in' | 'not_interested';

const PIPELINE_TABS: PipelineTab[] = ['all', 'interested', 'follow_up', 'app_out', 'app_in', 'not_interested'];

const PAGE_SIZE = 25;

function etCalendarDate(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function addCalendarDays(ymd: string, days: number): string {
  const parts = ymd.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return next.toISOString().slice(0, 10);
}

function utcDateFromYmd(ymd: string, endOfDay = false): Date {
  return new Date(endOfDay ? `${ymd}T23:59:59.999Z` : `${ymd}T00:00:00.000Z`);
}

function toYmd(d?: Date): string | undefined {
  if (!d) return undefined;
  return d.toISOString().slice(0, 10);
}

const FILTER_SELECT =
  'block h-9 w-full min-w-[10rem] rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50';

const PIPELINE_STATUSES: LeadStatus[] = ['interested', 'follow_up', 'app_out', 'app_in', 'not_interested'];

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'app_out', label: 'App Out' },
  { value: 'app_in', label: 'App In' },
  { value: 'unqualified', label: 'Unqualified' },
];

const MORE_STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'no_answer', label: 'No Answer' },
  { value: 'new', label: 'New' },
  { value: 'called', label: 'Called' },
  { value: 'busy', label: 'Voicemail' },
  { value: 'gatekeeper', label: 'Gatekeeper' },
  { value: 'operator', label: 'Operator' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'out_of_business', label: 'Out of Business' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'do_not_call', label: 'Do Not Call' },
  { value: 'deceased', label: 'Deceased' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'unreachable', label: 'Unreachable' },
  { value: 'failed', label: 'Failed' },
];

const ResultsPage: React.FC = () => {
  const { id: campaignId } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [callAttemptStats, setCallAttemptStats] = useState<CallAttemptStats | null>(null);
  const [tabCountMap, setTabCountMap] = useState<Record<string, number>>({});
  const [statusFilter, setStatusFilter] = useState<PipelineTab | LeadStatus>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [subStageFilter, setSubStageFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [subStages, setSubStages] = useState<LeadSubStage[]>([]);
  const [subStageCache, setSubStageCache] = useState<Record<string, LeadSubStage[]>>({});
  const [agents, setAgents] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [updatingLeadId, setUpdatingLeadId] = useState<string | null>(null);
  const loadedSubStageKeys = useRef(new Set<string>());

  const [activeQuickFilter, setActiveQuickFilter] = useState<'today' | 'yesterday' | 'all_time' | 'custom'>('all_time');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [filters, setFilters] = useState<CampaignResultFilters>({});
  const [pendingFilters, setPendingFilters] = useState<CampaignResultFilters>({});

  const isSmsCampaign = campaign?.campaignType === 'text';
  const activeMainStatus = statusFilter === 'all' ? undefined : statusFilter;

  useEffect(() => {
    agentService
      .getTagOptions()
      .then((all) => {
        const ids = campaign?.agentIds;
        if (ids?.length) {
          const idSet = new Set(ids);
          setAgents(all.filter((a) => idSet.has(a.id)));
        } else {
          setAgents(all);
        }
      })
      .catch(() => setAgents([]));
  }, [campaign?.agentIds]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchCampaign = useCallback(async () => {
    if (!campaignId) return;
    try {
      const data = await campaignService.getCampaign(campaignId);
      setCampaign(data);
    } catch {
      toast.error('Failed to load campaign');
    }
  }, [campaignId]);

  const fetchStats = useCallback(async (dateFilters: CampaignResultFilters) => {
    if (!campaignId) return;
    try {
      const [summary, counts] = await Promise.all([
        leadService.getResultsSummary(campaignId, {
          startDateTime: dateFilters.startDateTime,
          endDateTime: dateFilters.endDateTime,
        }),
        leadService.getLeadResultCounts({
          campaignId,
          agentId: agentFilter || undefined,
          dateFrom: toYmd(dateFilters.startDateTime),
          dateTo: toYmd(dateFilters.endDateTime),
        }),
      ]);
      setStats(summary.stats || null);
      setCallAttemptStats(summary.callAttemptStats || null);
      // Tab badges now count LEADS by their current stage (consistent with the list),
      // instead of mixing lead counts (Interested/Follow Up) with call-disposition
      // counts (App Out/App In) which caused the badge ≠ list mismatch.
      setTabCountMap(counts.statusCounts || {});
    } catch {
      /* non-fatal */
    }
  }, [campaignId, agentFilter]);

  const fetchLeads = useCallback(async (page = 1, opts?: { silent?: boolean }) => {
    if (!campaignId) return;
    try {
      if (!opts?.silent) setLoading(true);
      const statusList =
        statusFilter === 'all'
          ? PIPELINE_STATUSES
          : [statusFilter as LeadStatus];

      const output = await leadService.getAllLeads({
        campaignId,
        status: statusList,
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        subStageId: subStageFilter || undefined,
        agentId: agentFilter || undefined,
        dateFrom: toYmd(filters.startDateTime),
        dateTo: toYmd(filters.endDateTime),
        sortBy: 'statusChangedAt',
        sortOrder,
      });

      setLeads(output.data);
      setTotalCount(Number(output.pagination.total) || 0);
      setTotalPages(Number(output.pagination.totalPages) || 1);
      setCurrentPage(Number(output.pagination.page) || 1);
    } catch {
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [campaignId, statusFilter, debouncedSearch, subStageFilter, agentFilter, sortOrder, filters]);

  useEffect(() => {
    void fetchCampaign();
  }, [fetchCampaign]);

  useEffect(() => {
    if (campaignId && campaign && campaign.campaignType !== 'text') {
      void fetchStats(filters);
    }
  }, [campaignId, campaign, fetchStats, filters]);

  useEffect(() => {
    if (campaignId && campaign && campaign.campaignType !== 'text') {
      setCurrentPage(1);
      void fetchLeads(1);
    }
  }, [campaignId, campaign, fetchLeads]);

  useEffect(() => {
    if (!campaignId || !activeMainStatus) {
      setSubStages([]);
      setSubStageFilter('');
      return;
    }
    leadService
      .getSubStages({ campaignId, mainStatus: activeMainStatus })
      .then(setSubStages)
      .catch(() => setSubStages([]));
  }, [campaignId, activeMainStatus]);

  // Load sub-stage dropdown options once per campaign (all statuses in one request),
  // then group by main status — avoids a request per (campaign × status) combination.
  useEffect(() => {
    const pendingCampaigns = new Set<string>();
    for (const lead of leads) {
      if (lead.campaignId && !loadedSubStageKeys.current.has(lead.campaignId)) {
        pendingCampaigns.add(lead.campaignId);
      }
    }
    for (const cid of pendingCampaigns) {
      loadedSubStageKeys.current.add(cid);
      leadService
        .getSubStages({ campaignId: cid })
        .then((stages) => setSubStageCache((prev) => {
          const next = { ...prev };
          const byStatus: Record<string, LeadSubStage[]> = {};
          for (const s of stages) (byStatus[s.mainStatus] ??= []).push(s);
          for (const [st, list] of Object.entries(byStatus)) next[`${cid}:${st}`] = list;
          return next;
        }))
        .catch(() => undefined);
    }
  }, [leads]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchStats(filters), fetchLeads(currentPage)]);
    setRefreshing(false);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    void fetchLeads(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLeadUpdate = async (
    lead: Lead,
    patch: { status?: LeadStatus; subStageId?: string | null },
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    e.stopPropagation();
    setUpdatingLeadId(lead.id);
    try {
      await leadService.updateLead(lead.id, patch);
      if (
        patch.status &&
        PIPELINE_STATUSES.includes(patch.status)
      ) {
        setStatusFilter(patch.status as PipelineTab);
        setCurrentPage(1);
        await fetchLeads(1, { silent: true });
      } else {
        await fetchLeads(currentPage, { silent: true });
      }
      await fetchStats(filters);
    } catch {
      toast.error('Failed to update lead');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  const handleDelete = async (leadId: string) => {
    if (!window.confirm('Move this lead to trash? You can restore it later.')) return;
    try {
      await leadService.deleteLead(leadId);
      toast.success('Lead moved to trash');
      await fetchLeads(currentPage, { silent: true });
      await fetchStats(filters);
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const handleExport = async () => {
    if (!campaignId) return;
    try {
      setExporting(true);
      await leadService.exportLeads(campaignId, {
        format: 'csv',
        fields: ['id', 'fullName', 'phoneE164', 'email', 'companyName', 'status', 'lastCallAt', 'createdAt'],
        filters: {
          status: statusFilter === 'all' ? PIPELINE_STATUSES : [statusFilter as LeadStatus],
          agentId: agentFilter || undefined,
          subStageId: subStageFilter || undefined,
          search: debouncedSearch || undefined,
        },
      });
      toast.success('Export started');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const applyQuickFilter = (key: 'today' | 'yesterday' | 'all_time' | 'custom') => {
    setActiveQuickFilter(key);
    if (key === 'custom') {
      setShowDateFilters(true);
      return;
    }
    setShowDateFilters(false);
    const ymdToday = etCalendarDate();
    let next: CampaignResultFilters = {};
    if (key === 'today') {
      next = {
        startDateTime: utcDateFromYmd(ymdToday),
        endDateTime: utcDateFromYmd(ymdToday, true),
      };
    } else if (key === 'yesterday') {
      const ymd = addCalendarDays(ymdToday, -1);
      next = {
        startDateTime: utcDateFromYmd(ymd),
        endDateTime: utcDateFromYmd(ymd, true),
      };
    }
    setFilters(next);
    setPendingFilters(next);
  };

  const interestedCount = tabCountMap.interested ?? stats?.interestedLeads ?? 0;
  const followUpCount = tabCountMap.follow_up ?? stats?.followUpLeads ?? 0;
  const notInterestedCount = tabCountMap.not_interested ?? stats?.notInterestedLeads ?? 0;
  const appOutCount = tabCountMap.app_out ?? callAttemptStats?.appOut ?? 0;
  const appInCount = tabCountMap.app_in ?? callAttemptStats?.appIn ?? 0;
  const allCount = interestedCount + followUpCount + appOutCount + appInCount + notInterestedCount;

  const agentOptions = useMemo(
    () => agents.map((a) => ({ value: a.id, label: a.name, keywords: a.email })),
    [agents]
  );

  // Full lead-outcome breakdown by current stage (mirrors the Live Monitor breakdown
  // and stays consistent with the stat cards + pipeline tab counts above).
  const statusBreakdown = useMemo(() => {
    const defs: { key: string; label: string; bar: string }[] = [
      { key: 'interested', label: 'Interested', bar: 'bg-emerald-500' },
      { key: 'not_interested', label: 'Not Interested', bar: 'bg-red-500' },
      { key: 'follow_up', label: 'Follow Up', bar: 'bg-purple-500' },
      { key: 'app_in', label: 'App In', bar: 'bg-sky-500' },
      { key: 'app_out', label: 'App Out', bar: 'bg-indigo-500' },
      { key: 'called', label: 'Called', bar: 'bg-green-500' },
      { key: 'no_answer', label: 'No Answer', bar: 'bg-orange-500' },
      { key: 'busy', label: 'Busy', bar: 'bg-amber-500' },
      { key: 'gatekeeper', label: 'Gatekeeper', bar: 'bg-cyan-500' },
      { key: 'operator', label: 'Operator', bar: 'bg-teal-500' },
      { key: 'wrong_number', label: 'Wrong Number', bar: 'bg-pink-500' },
      { key: 'out_of_business', label: 'Out of Business', bar: 'bg-gray-500' },
      { key: 'unqualified', label: 'Unqualified', bar: 'bg-slate-500' },
      { key: 'do_not_call', label: 'Do Not Call', bar: 'bg-red-700' },
      { key: 'deceased', label: 'Deceased', bar: 'bg-stone-500' },
      { key: 'spanish', label: 'Spanish', bar: 'bg-rose-500' },
      { key: 'hangup', label: 'Hang Up', bar: 'bg-orange-700' },
      { key: 'unreachable', label: 'Unreachable', bar: 'bg-gray-400' },
      { key: 'failed', label: 'Failed', bar: 'bg-red-600' },
    ];
    return defs.map((d) => ({ ...d, value: tabCountMap[d.key] || 0 }));
  }, [tabCountMap]);

  const statusBreakdownTotal = useMemo(
    () => statusBreakdown.reduce((sum, i) => sum + i.value, 0),
    [statusBreakdown]
  );

  const pipelineTabs: {
    id: PipelineTab;
    label: string;
    count: number;
    icon: React.ComponentType<{ className?: string }>;
    activeColor: string;
  }[] = [
    { id: 'all', label: 'All Pipeline', count: allCount, icon: FunnelIcon, activeColor: 'bg-primary text-primary-foreground' },
    { id: 'interested', label: 'Interested', count: interestedCount, icon: CheckCircleIcon, activeColor: 'bg-green-600 text-white dark:bg-green-500' },
    { id: 'follow_up', label: 'Follow Up', count: followUpCount, icon: PlayCircleIcon, activeColor: 'bg-blue-600 text-white dark:bg-blue-500' },
    { id: 'app_out', label: 'App Out', count: appOutCount, icon: ClockIcon, activeColor: 'bg-indigo-600 text-white dark:bg-indigo-500' },
    { id: 'app_in', label: 'App In', count: appInCount, icon: ClockIcon, activeColor: 'bg-sky-600 text-white dark:bg-sky-500' },
    { id: 'not_interested', label: 'Not Interested', count: notInterestedCount, icon: CheckCircleIcon, activeColor: 'bg-muted-foreground text-white' },
  ];

  if (!campaignId) {
    return <div className="p-8 text-center text-muted-foreground">Campaign not found.</div>;
  }

  if (!campaign) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isSmsCampaign) {
    return <SmsCampaignResultsView campaignId={campaignId} campaign={campaign} />;
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'Campaigns', href: '/campaigns' },
          { label: campaign?.name || 'Campaign', href: `/campaigns/${campaignId}` },
          { label: 'Results', current: true },
        ]}
      />

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{campaign?.name || 'Campaign Results'}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pipeline results — click a lead for timeline, notes, and call history.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outlined" size="sm" onClick={() => void handleRefresh()} loading={refreshing}>
            <ArrowPathIcon className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button variant="outlined" size="sm" onClick={() => void handleExport()} loading={exporting}>
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Export
          </Button>
        </div>
      </div>

      {callAttemptStats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total Leads', value: (callAttemptStats.totalLeads || 0).toLocaleString(), hint: 'Live list' },
            { label: 'Remaining', value: (callAttemptStats.remainingLeads || 0).toLocaleString(), hint: 'Still new' },
            {
              label: 'Calls Made',
              value: (callAttemptStats.totalCalls || 0).toLocaleString(),
              hint: `${(callAttemptStats.uniqueLeadsDialed ?? 0).toLocaleString()} unique people`,
            },
            {
              label: 'Pickup',
              value: (callAttemptStats.pickups ?? 0).toLocaleString(),
              hint: 'Merchants who answered',
            },
            {
              label: 'Connected',
              value: (callAttemptStats.answered || 0).toLocaleString(),
              hint: 'Agent disposition recorded',
            },
            {
              label: 'Connect rate',
              value: `${
                callAttemptStats.totalCalls > 0
                  ? ((callAttemptStats.answered / callAttemptStats.totalCalls) * 100).toFixed(1)
                  : '0'
              }%`,
              hint: 'dispositions per attempts',
            },
          ].map((item) => (
            <div key={item.label} className="rounded-xl border border-border bg-card px-4 py-3">
              <p className="text-2xl font-bold text-foreground">{item.value}</p>
              <p className="mt-1 text-xs font-medium text-muted-foreground">{item.label}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground/80">{item.hint}</p>
            </div>
          ))}
        </div>
      )}

      {statusBreakdownTotal > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Lead Outcome Breakdown
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({statusBreakdownTotal.toLocaleString()} leads)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statusBreakdown.map((item) => {
                const pct = statusBreakdownTotal > 0 ? Math.round((item.value / statusBreakdownTotal) * 100) : 0;
                return (
                  <div key={item.key} className="flex items-center gap-3">
                    <div className="w-28 shrink-0 text-sm text-muted-foreground">{item.label}</div>
                    <div className="h-5 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${item.bar}`}
                        style={{ width: `${item.value > 0 ? Math.max(pct, 1) : 0}%` }}
                      />
                    </div>
                    <div className="w-20 text-right text-sm">
                      <span className="font-semibold text-foreground">{item.value.toLocaleString()}</span>
                      <span className="ml-1 text-xs text-muted-foreground">({pct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[180px]">
            <SearchableSelect
              id="results-agent-filter"
              label="Filter by agent"
              value={agentFilter}
              onChange={(v) => { setAgentFilter(v); setCurrentPage(1); }}
              options={agentOptions}
              allOption={{ value: '', label: 'All agents' }}
              searchPlaceholder="Search agents…"
              emptyMessage="No agents found"
            />
          </div>

          <div className="min-w-[180px]">
            <label htmlFor="results-status-filter" className="block text-xs font-medium text-muted-foreground mb-1">
              More statuses
            </label>
            <select
              id="results-status-filter"
              className={FILTER_SELECT}
              value={PIPELINE_TABS.includes(statusFilter as PipelineTab) ? '' : statusFilter}
              onChange={(e) => {
                const v = e.target.value;
                if (v) {
                  setStatusFilter(v as LeadStatus);
                  setCurrentPage(1);
                  setSubStageFilter('');
                }
              }}
            >
              <option value="">Pipeline tabs above</option>
              {MORE_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[220px] max-w-md">
            <label htmlFor="results-search" className="block text-xs font-medium text-muted-foreground mb-1">
              Search
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                id="results-search"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name, phone, company, email..."
                className="input w-full pl-9 text-sm"
              />
            </div>
          </div>

          <div className="min-w-[160px]">
            <label htmlFor="results-sort" className="block text-xs font-medium text-muted-foreground mb-1">
              Sort by status change
            </label>
            <select
              id="results-sort"
              className={FILTER_SELECT}
              value={sortOrder}
              onChange={(e) => { setSortOrder(e.target.value as 'desc' | 'asc'); setCurrentPage(1); }}
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
        </div>
      </div>

      {/* Date filter — positioned like the All Results page (below filters, above tabs) */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center rounded-lg border border-border overflow-hidden w-fit">
          {(['today', 'yesterday', 'all_time', 'custom'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => applyQuickFilter(f)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                activeQuickFilter === f ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted/40'
              } ${f !== 'today' ? 'border-l border-border' : ''}`}
            >
              {f === 'today' ? 'Today' : f === 'yesterday' ? 'Yesterday' : f === 'all_time' ? 'All Time' : 'Custom'}
            </button>
          ))}
        </div>
        {showDateFilters && activeQuickFilter === 'custom' && (
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card px-3 py-2">
            <div>
              <label className="text-xs text-muted-foreground">From</label>
              <input
                type="datetime-local"
                className="input block mt-1 text-sm"
                value={pendingFilters.startDateTime?.toISOString().slice(0, 16) || ''}
                onChange={(e) =>
                  setPendingFilters((p) => ({
                    ...p,
                    startDateTime: e.target.value ? new Date(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">To</label>
              <input
                type="datetime-local"
                className="input block mt-1 text-sm"
                value={pendingFilters.endDateTime?.toISOString().slice(0, 16) || ''}
                onChange={(e) =>
                  setPendingFilters((p) => ({
                    ...p,
                    endDateTime: e.target.value ? new Date(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <Button size="sm" onClick={() => { setFilters(pendingFilters); }}>
              Apply
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1 bg-muted rounded-lg p-1">
        {pipelineTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = statusFilter === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => { setStatusFilter(tab.id); setCurrentPage(1); setSubStageFilter(''); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                isActive ? tab.activeColor + ' shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-muted-foreground/10'}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {subStages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => { setSubStageFilter(''); setCurrentPage(1); }}
            className={`text-xs px-3 py-1 rounded-full border ${!subStageFilter ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}
          >
            All sub-stages
          </button>
          {subStages.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { setSubStageFilter(s.id); setCurrentPage(1); }}
              className={`text-xs px-3 py-1 rounded-full border ${subStageFilter === s.id ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Results
              <span className="ml-2 text-base font-normal text-muted-foreground">({totalCount.toLocaleString()})</span>
            </CardTitle>
            {totalPages > 1 && (
              <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <FunnelIcon className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground font-medium">No leads found</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {leads.map((lead) => (
                  <LeadResultCard
                    key={lead.id}
                    lead={lead}
                    statusOptions={STATUS_OPTIONS}
                    subStageOptions={subStageCache[`${lead.campaignId}:${lead.status}`] || []}
                    updating={updatingLeadId === lead.id}
                    enableContactActions
                    onLeadFieldUpdated={() => fetchLeads(currentPage)}
                    onDelete={isAdmin ? () => handleDelete(lead.id) : undefined}
                    onViewDetails={() => setSelectedLeadId(lead.id)}
                    onStatusChange={(status, e) => handleLeadUpdate(lead, { status }, e)}
                    onSubStageChange={(subStageId, e) => handleLeadUpdate(lead, { subStageId }, e)}
                    onTagsUpdated={() => fetchLeads(currentPage, { silent: true })}
                    onFollowUpUpdated={() => fetchLeads(currentPage, { silent: true })}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outlined" size="sm" disabled={currentPage <= 1} onClick={() => handlePageChange(currentPage - 1)}>
                      <ChevronLeftIcon className="h-4 w-4 mr-1" /> Previous
                    </Button>
                    <Button variant="outlined" size="sm" disabled={currentPage >= totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                      Next <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <LeadDetailSidebar
        leadId={selectedLeadId}
        enableContactActions
        onClose={() => setSelectedLeadId(null)}
        onLeadUpdated={() => { void fetchLeads(currentPage, { silent: true }); void fetchStats(filters); }}
      />
    </div>
  );
};

export default ResultsPage;
